// View state: what is selected and what is under the pointer. Separate from
// the game so a replay or a socket never sees it.

import { SELECTION_KEYS } from '../constants';
import { eligibleTargets, legalMovesFrom } from '../game/flow';
import { createStore } from '../lib/store';
import { playSound } from '../sound/player';
import { aimAt, gameStore, playMove } from './game';
import { historyStore, isReviewing } from './history';
import { actsFor } from './localSide';

export type UiState = {
  selected: number | null;
  hovered: number | null;
};

export const uiStore = createStore<UiState>({ selected: null, hovered: null });

export const hoverSquare = (square: number | null): void =>
  uiStore.set((ui) => (ui.hovered === square ? ui : { ...ui, hovered: square }));

export const clearSelection = (): void =>
  uiStore.set((ui) => (ui.selected === null ? ui : { ...ui, selected: null }));

export const clickSquare = (square: number): void => {
  if (isReviewing()) return;
  const game = gameStore.get();
  if (game.phase === 'over') return;
  const mine = actsFor(game.turn);

  if (mine && game.phase === 'aim' && eligibleTargets(game).includes(square)) {
    aimAt(square);
    return;
  }

  const { selected } = uiStore.get();
  if (mine && game.phase === 'move' && selected !== null) {
    const move = legalMovesFrom(game, selected).find((candidate) => candidate.to === square);
    if (move) {
      clearSelection();
      playMove(move);
      return;
    }
  }
  // Any piece can be picked up to read, in any phase and on either side's turn. A touch
  // screen has no hover, so this is the only way to read a piece there. Only a movable
  // one shows destinations.
  const canSelect = game.board[square] !== null;
  if (selected === square) {
    // A second click on the picked piece puts it down. The pointer is still on the square,
    // so the hover goes too. If it stayed, the board would draw the same destinations as a
    // preview and the piece would still look picked.
    uiStore.set(() => ({ selected: null, hovered: null }));
    return;
  }
  // A piece comes up. A tap on an empty square that is no move and no target does nothing.
  playSound(canSelect ? 'lift' : 'refuse');
  uiStore.set((ui) => ({ ...ui, selected: canSelect ? square : null }));
};

/** Call once at startup. Escape puts the picked piece down. */
export const startSelectionKeys = (): void => {
  window.addEventListener('keydown', (event) => {
    if (event.key === SELECTION_KEYS.clear) clearSelection();
  });
};

// A new game state can make the selection stale. So can a step back. Drop it.
gameStore.subscribe(() => clearSelection());
historyStore.subscribe(() => clearSelection());
